import {
	createObjectUrlFromBytes,
	detectContentTypeFromBytes,
	inferContentType,
	isClientDecryptSupported,
	restoreProtectedAssetBytes,
	revokeObjectUrl
} from './assetCrypto';
import { normalizeProtectedAssetPath } from './protectedAssetUrl';

const RESOLVED_CACHE = new Map();
const MAX_CACHE_SIZE = 240;
const PENDING_CACHE = new Map();
const REVOKABLE_URLS = new Set();

function hasAssetSource(source) {
	return typeof source === 'string' && source.trim().length > 0;
}

function createResolvedEntry(source, extra = {}) {
	const normalizedSource = typeof source === 'string' ? source.trim() : '';
	const normalizedUrl = normalizeAssetUrl(normalizedSource);
	const fileName = extra.fileName || parseFileNameFromSource(normalizedSource);
	return {
		source: normalizedSource,
		url: normalizedUrl,
		fileName,
		contentType: inferContentType(extra.contentType, fileName, extra.kind),
		mode: 'DIRECT',
		blob: null,
		protected: false,
		revoke: false
	};
}

function normalizeAssetUrl(url) {
	return normalizeProtectedAssetPath(url);
}

function touchResolvedCache(cacheKey, entry) {
	RESOLVED_CACHE.delete(cacheKey);
	RESOLVED_CACHE.set(cacheKey, entry);
	return entry;
}

function revokeResolvedEntry(entry) {
	if (!entry || !entry.revoke) {
		return;
	}
	REVOKABLE_URLS.delete(entry.url);
	revokeObjectUrl(entry.url);
}

function trimResolvedCache() {
	while (RESOLVED_CACHE.size > MAX_CACHE_SIZE) {
		const oldestKey = RESOLVED_CACHE.keys().next().value;
		// 仅移出解析缓存，不提前 revoke，避免页面仍在使用的 blob URL 被淘汰后失效。
		RESOLVED_CACHE.delete(oldestKey);
	}
}

function parseFileNameFromContentDisposition(contentDisposition) {
	const raw = String(contentDisposition || '');
	if (!raw) {
		return '';
	}
	const utf8Match = raw.match(/filename\*=UTF-8''([^;]+)/i);
	if (utf8Match && utf8Match[1]) {
		try {
			return decodeURIComponent(utf8Match[1].trim());
		} catch (error) {
			return utf8Match[1].trim();
		}
	}
	const normalMatch = raw.match(/filename="?([^"]+)"?/i);
	return normalMatch && normalMatch[1] ? normalMatch[1].trim() : '';
}

function parseFileNameFromSource(source) {
	const normalized = String(source || '').trim();
	if (!normalized) {
		return '';
	}
	const noQuery = normalized.split('?')[0].split('#')[0];
	const idx = noQuery.lastIndexOf('/');
	if (idx < 0) {
		return noQuery;
	}
	return noQuery.slice(idx + 1);
}

function buildFetchHeaders(extra = {}) {
	const headers = {};
	if (extra.accept) {
		headers.Accept = extra.accept;
	}
	return headers;
}

async function fetchAndRestore(source, extra = {}) {
	const resolvedUrl = normalizeAssetUrl(source);
	if (!resolvedUrl) {
		return createResolvedEntry('', extra);
	}
	if (resolvedUrl.startsWith('blob:') || resolvedUrl.startsWith('data:')) {
		return {
			...createResolvedEntry(source, extra),
			url: resolvedUrl
		};
	}
	const response = await fetch(resolvedUrl, {
		method: 'GET',
		mode: 'cors',
		cache: 'force-cache',
		credentials: 'omit',
		headers: buildFetchHeaders(extra)
	});
	if (!response.ok) {
		throw new Error(`资源读取失败(${response.status})`);
	}
	const payload = await response.arrayBuffer();
	const restored = restoreProtectedAssetBytes(payload);
	const responseContentType = response.headers.get('content-type');
	const headerFileName = parseFileNameFromContentDisposition(response.headers.get('content-disposition'));
	const fileName = extra.fileName || headerFileName || parseFileNameFromSource(source);
	const fallbackContentType = inferContentType(extra.contentType || responseContentType, fileName, extra.kind);
	const contentType = detectContentTypeFromBytes(restored.bytes, extra.kind, fallbackContentType);
	const objectUrl = createObjectUrlFromBytes(restored.bytes, contentType);
	REVOKABLE_URLS.add(objectUrl.url);
	return {
		source: String(source || '').trim(),
		url: objectUrl.url,
		fileName,
		contentType,
		mode: restored.protected ? 'CLIENT_RESTORE' : 'CLIENT_FETCH',
		blob: objectUrl.blob,
		protected: restored.protected,
		revoke: true
	};
}

async function resolve(source, extra = {}) {
	if (!hasAssetSource(source)) {
		return createResolvedEntry('', extra);
	}
	const normalizedSource = source.trim();
	const cacheKey = JSON.stringify({
		source: normalizedSource,
		fileName: extra.fileName || '',
		contentType: extra.contentType || '',
		kind: extra.kind || ''
	});
	if (!extra.forceRefresh) {
		const cached = RESOLVED_CACHE.get(cacheKey);
		if (cached) {
			if (extra.fileName && !cached.fileName) {
				cached.fileName = extra.fileName;
			}
			if (extra.contentType && !cached.contentType) {
				cached.contentType = extra.contentType;
			}
			return touchResolvedCache(cacheKey, cached);
		}
		const pending = PENDING_CACHE.get(cacheKey);
		if (pending) {
			return pending;
		}
	}
	const pending = fetchAndRestore(normalizedSource, extra).then((resolvedEntry) => {
		touchResolvedCache(cacheKey, resolvedEntry);
		trimResolvedCache();
		return resolvedEntry;
	}).finally(() => {
		PENDING_CACHE.delete(cacheKey);
	});
	PENDING_CACHE.set(cacheKey, pending);
	return pending;
}

async function resolveUrl(source, extra = {}) {
	const resolved = await resolve(source, extra);
	return resolved.url;
}

function triggerBrowserDownload(url, fileName) {
	const link = document.createElement('a');
	link.style.display = 'none';
	link.href = url;
	link.target = '_blank';
	if (fileName) {
		link.download = fileName;
	}
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
}

async function download(source, extra = {}) {
	const resolved = await resolve(source, extra);
	if (!resolved.url) {
		throw new Error('文件地址为空');
	}
	triggerBrowserDownload(resolved.url, extra.fileName || resolved.fileName);
	return resolved;
}

function clear() {
	PENDING_CACHE.clear();
	RESOLVED_CACHE.forEach((entry) => revokeResolvedEntry(entry));
	RESOLVED_CACHE.clear();
	REVOKABLE_URLS.forEach((url) => revokeObjectUrl(url));
	REVOKABLE_URLS.clear();
}

if (typeof window !== 'undefined') {
	window.addEventListener('beforeunload', clear);
}

export default {
	hasAssetSource,
	isClientDecryptSupported,
	resolve,
	resolveUrl,
	download,
	clear
};
